import { FC, MouseEvent, RefObject, useMemo, useRef } from 'react';
import { type ScrollTriggerPosition, StyledScrollTrigger } from './styles';
import { DraggableData } from 'react-draggable';
import useEndsOfScroll from 'hooks/useEndsOfScroll';

type BaseScrollTriggerProps = {
  scrollContainerRef?: RefObject<HTMLElement>;
  size?: number;
  zIndex?: number;
  enabled?: boolean;
  isFixed?: boolean;
  adjustDragItem?: boolean;
  dragDataRef?: RefObject<DraggableData>;
  scrollSpeedCoefficient?: number;
};

type PositionProps = {
  position: ScrollTriggerPosition;
  offset?: number;
};

type ScrollTriggerProps = BaseScrollTriggerProps & PositionProps;

const SCROLL_SPEED_COEFFICIENT = 0.2;
const SCROLL_TRIGGER_SIZE = 90;

export const ScrollTrigger: FC<ScrollTriggerProps> = ({
  scrollContainerRef,
  size = SCROLL_TRIGGER_SIZE,
  position,
  zIndex = 1,
  enabled = false,
  offset = 0,
  isFixed = false,
  adjustDragItem = false,
  dragDataRef,
  scrollSpeedCoefficient = SCROLL_SPEED_COEFFICIENT,
}) => {
  const scrollerIntervalRef = useRef<NodeJS.Timeout>();
  const scrollSpeedRef = useRef<number>(1);
  const enterRef = useRef<{
    coord: number;
    initialScrollOffset: number;
  }>({ coord: 0, initialScrollOffset: 0 });

  const [isVertical, isNegative] = useMemo(() => {
    return [
      ['top', 'bottom'].includes(position),
      ['top', 'left'].includes(position),
    ];
  }, [position]);

  const onMouseEnter = (e: MouseEvent<HTMLElement>) => {
    if (e.currentTarget) {
      const { [isVertical ? 'clientY' : 'clientX']: clientOffset } = e;
      const { [position]: triggerEnd } =
        e.currentTarget.getBoundingClientRect();

      const initialScrollOffset = scrollContainerRef?.current
        ? scrollContainerRef.current[isVertical ? 'scrollTop' : 'scrollLeft']
        : window[isVertical ? 'scrollY' : 'scrollX'];

      const coord = isVertical ? 'y' : 'x';

      enterRef.current =
        adjustDragItem && dragDataRef?.current
          ? {
              coord:
                dragDataRef.current[coord] -
                (size + (clientOffset - triggerEnd) * (isNegative ? -1 : 1)),
              initialScrollOffset,
            }
          : { coord: 0, initialScrollOffset };
    }
    // if scrollContainerRef is provided, scroll parent element, otherwise scroll window
    if (scrollContainerRef?.current) {
      scrollerIntervalRef.current = setInterval(() => {
        if (scrollContainerRef.current && enabled) {
          const scrollOffsetProp = isVertical ? 'scrollTop' : 'scrollLeft';
          const clientSizeProp = isVertical ? 'clientHeight' : 'clientWidth';
          const scrollSizeProp = isVertical ? 'scrollHeight' : 'scrollWidth';

          const maxScrollOffset =
            scrollContainerRef.current[scrollSizeProp] -
            scrollContainerRef.current[clientSizeProp];

          const scrollOffset = Math.min(
            Math.max(
              scrollContainerRef.current[scrollOffsetProp] +
                Math.ceil(
                  scrollSpeedRef.current *
                    ((isNegative ? -1 : 1) * scrollSpeedCoefficient)
                ),
              0
            ),
            maxScrollOffset
          );

          scrollContainerRef.current[scrollOffsetProp] = scrollOffset;

          if (adjustDragItem && dragDataRef?.current) {
            const { coord, initialScrollOffset } = enterRef.current;
            const adjust =
              coord +
              (scrollOffset - initialScrollOffset) +
              scrollSpeedRef.current * (isNegative ? -1 : 1);

            dragDataRef.current.node.setAttribute(
              'style',
              `transform: translate(${isVertical ? dragDataRef.current.x : adjust}px, ${isVertical ? adjust : dragDataRef.current.y}px);`
            );
          }
        } else {
          clearInterval(scrollerIntervalRef.current);
        }
      }, 40 * scrollSpeedCoefficient);
    } else {
      scrollerIntervalRef.current = setInterval(() => {
        if (enabled) {
          const by = Math.ceil(
            scrollSpeedRef.current *
              ((isNegative ? -1 : 1) * scrollSpeedCoefficient)
          );
          window.scrollBy(isVertical ? 0 : by, isVertical ? by : 0);

          if (adjustDragItem && dragDataRef?.current) {
            const { coord, initialScrollOffset } = enterRef.current;
            const adjust =
              coord +
              (window[isVertical ? 'scrollY' : 'scrollX'] -
                initialScrollOffset) +
              scrollSpeedRef.current * (isNegative ? -1 : 1);

            dragDataRef.current.node.setAttribute(
              'style',
              `transform: translate(${isVertical ? dragDataRef.current.x : adjust}px, ${isVertical ? adjust : dragDataRef.current.y}px);`
            );
          }
        } else {
          clearInterval(scrollerIntervalRef.current);
        }
      }, 40 * scrollSpeedCoefficient);
    }
  };

  const onMouseLeave = () => {
    clearInterval(scrollerIntervalRef.current);
  };

  const onMouseMove = (e: MouseEvent<HTMLElement>) => {
    if (enabled) {
      const clientCoord = isVertical ? 'clientY' : 'clientX';
      const { [position]: triggerEnd } =
        e.currentTarget.getBoundingClientRect();
      scrollSpeedRef.current =
        size + (e[clientCoord] - triggerEnd) * (isNegative ? -1 : 1);
    }
  };

  return (
    <StyledScrollTrigger
      size={size}
      position={position}
      isActive={enabled}
      zIndex={zIndex}
      offset={offset}
      isFixed={isFixed}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    />
  );
};

type ScrollTriggersProps = BaseScrollTriggerProps & {
  scrollTriggers: PositionProps[];
};

const dummyRef = { current: null };

export const ScrollTriggers: FC<ScrollTriggersProps> = ({
  scrollTriggers,
  scrollContainerRef,
  enabled,
  ...props
}) => {
  const [top, right, bottom, left] = useEndsOfScroll(
    scrollContainerRef || dummyRef,
    []
  );
  const scrollEnds = { top, right, bottom, left };

  return (
    <>
      {scrollTriggers.map(({ position, offset }, index) => (
        <ScrollTrigger
          key={index}
          scrollContainerRef={scrollContainerRef}
          enabled={enabled && !scrollEnds[position]}
          position={position}
          offset={offset}
          {...props}
        />
      ))}
    </>
  );
};
